import { ApiError } from '@cmms/api-client'

const DEFAULT_FALLBACK = 'Something went wrong. Please try again.'

/**
 * Turn any error / message into something safe to show a real user.
 * Filters out raw stack traces, SQL errors, Node-style exception text
 * etc. — even when the server sent them in the response body. When the
 * input looks technical we drop it and fall back to a polite generic
 * message (or a status-based one if we have an [ApiError]).
 *
 * Pass either:
 *   - a string that came from somewhere already (gets filtered)
 *   - an Error / ApiError directly (we extract `.message` + use status
 *     to pick a sensible default if filtering rejects the raw text)
 *   - `null` / `undefined` (returns `fallback`)
 */
export function friendlyMessage(input: unknown, fallback = DEFAULT_FALLBACK): string {
  if (input == null) return fallback

  // ApiError: prefer status-based defaults when the server's message
  // doesn't pass the user-friendliness check.
  if (input instanceof ApiError) {
    const status = input.status
    const raw = (input.message ?? '').trim()
    if (raw && looksUserFriendly(raw)) return raw
    return statusFallback(status, fallback)
  }

  if (input instanceof Error) {
    const raw = (input.message ?? '').trim()
    return raw && looksUserFriendly(raw) ? raw : fallback
  }

  if (typeof input === 'string') {
    const raw = input.trim()
    return raw && looksUserFriendly(raw) ? raw : fallback
  }

  return fallback
}

function statusFallback(status: number, fallback: string): string {
  if (status === 0)   return 'No connection. Check your internet and try again.'
  if (status === 401) return 'Your session expired. Please sign in again.'
  if (status === 403) return "You don't have permission to do that."
  if (status === 404) return "We couldn't find that. It may have been removed."
  if (status === 409) return 'That conflicts with something already there.'
  if (status === 413) return 'That file is too big. Try a smaller one.'
  if (status === 422) return 'Some fields need attention. Please review and try again.'
  if (status === 429) return 'Too many requests. Please wait a moment and try again.'
  if (status >= 500 && status < 600) {
    // We deliberately DON'T trust the body message on 5xx — even
    // tame-looking text often leaks internals from a thrown error.
    return "The server's having trouble right now. Please try again in a bit."
  }
  return fallback
}

/**
 * Heuristic: does this string look like something we'd be willing to
 * show a non-technical user?
 *
 * We REJECT messages with common technical telltales (stack traces,
 * SQL/ORM errors, Node exceptions, file paths, JSON dumps). False
 * positives are fine — they just route through a generic message,
 * which is the safe default.
 */
function looksUserFriendly(s: string): boolean {
  if (!s || s.length > 200) return false
  const lower = s.toLowerCase()
  const banned = [
    // Node/V8 runtime
    'cannot read property',
    'cannot read properties',
    'is not a function',
    'is not defined',
    'is not iterable',
    'typeerror',
    'referenceerror',
    'syntaxerror',
    'rangeerror',
    'unhandled promise',
    '[object object]',
    'undefined is not',
    'null is not',
    // Postgres / ORM
    'syntax error at',
    'column "',
    'relation "',
    'duplicate key value',
    'violates foreign key',
    'violates not-null',
    'invalid input syntax',
    'pg_',
    'econnrefused',
    'enotfound',
    'etimedout',
    // Fastify / generic server signatures
    'fastreply',
    'at function ',
    'at async ',
    'at object.',
    '.js:',
    '.ts:',
    'stack trace',
    'request failed with',
    // File-system path leaks
    '/users/',
    '/var/',
    '/etc/',
    'node_modules',
    'c:\\',
    // Raw JSON dumps
    'statuscode',
  ]
  for (const marker of banned) {
    if (lower.includes(marker)) return false
  }
  if ((s.match(/\n/g) ?? []).length > 2) return false
  return true
}
